import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { MapPinIcon, CircleIcon } from 'lucide-react';
const targetingTiers = [{
  tier: 'Primary',
  description: 'Core service area',
  locations: ['San Ramon', 'Danville', 'Dublin', 'Pleasanton', 'Walnut Creek'],
  color: '#1B2A4A',
  bgColor: '#1B2A4A'
}, {
  tier: 'Secondary',
  description: 'Extended reach',
  locations: ['Alamo', 'Blackhawk', 'Livermore', 'Castro Valley'],
  color: '#C9A96E',
  bgColor: '#C9A96E'
}, {
  tier: 'Optional Expansion',
  description: 'Future growth',
  locations: ['Concord', 'Martinez', 'Fremont'],
  color: '#A8B4C4',
  bgColor: '#A8B4C4'
}];
export function GeoTargeting() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="targeting" className="py-24 md:py-32 bg-[#F8F9FA]" ref={ref} data-id="element-54">
      <div className="max-w-6xl mx-auto px-6" data-id="element-55">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-56">
          <p className="text-[#C9A96E] text-sm font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-57">
            Targeting
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#1B2A4A]" data-id="element-58">
            Geographic Targeting
          </h2>
          <p className="text-[#666] mt-4 max-w-2xl mx-auto" data-id="element-59">
            We'll target patients physically located in the communities most
            likely to visit Be Clinical.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-60">
          {/* Concentric Rings Visualization */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative aspect-square max-w-md mx-auto" data-id="element-61">
            {/* Outer ring - Optional */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={isInView ? {
            scale: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="absolute inset-0 rounded-full border-2 border-dashed border-[#A8B4C4]/50 bg-[#A8B4C4]/5" data-id="element-62" />

            {/* Middle ring - Secondary */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={isInView ? {
            scale: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="absolute inset-[15%] rounded-full border-2 border-[#C9A96E]/50 bg-[#C9A96E]/10" data-id="element-63" />

            {/* Inner ring - Primary */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={isInView ? {
            scale: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="absolute inset-[30%] rounded-full border-2 border-[#1B2A4A] bg-[#1B2A4A]/10" data-id="element-64" />

            {/* Center - Be Clinical */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={isInView ? {
            scale: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="absolute inset-[42%] rounded-full bg-[#1B2A4A] flex items-center justify-center shadow-lg" data-id="element-65">
              <MapPinIcon className="w-8 h-8 text-[#C9A96E]" data-id="element-66" />
            </motion.div>

            {/* Labels */}
            <motion.div initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="absolute top-2 left-1/2 -translate-x-1/2 text-xs text-[#A8B4C4] font-medium" data-id="element-67">
              Optional Expansion
            </motion.div>
            <motion.div initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.7
          }} className="absolute top-[18%] left-1/2 -translate-x-1/2 text-xs text-[#C9A96E] font-medium" data-id="element-68">
              Secondary
            </motion.div>
            <motion.div initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="absolute top-[33%] left-1/2 -translate-x-1/2 text-xs text-[#1B2A4A] font-medium" data-id="element-69">
              Primary
            </motion.div>

            {/* Radius indicator */}
            <motion.div initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.9
          }} className="absolute bottom-4 right-4 bg-white rounded-lg px-3 py-2 shadow-md" data-id="element-70">
              <p className="text-xs text-[#666]" data-id="element-71">Radius</p>
              <p className="text-sm font-semibold text-[#1B2A4A]" data-id="element-72">
                15–20 miles
              </p>
            </motion.div>
          </motion.div>

          {/* Targeting Tiers */}
          <div className="space-y-6" data-id="element-73">
            {targetingTiers.map((tier, index) => <motion.div key={tier.tier} initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.3 + index * 0.15
          }} className="bg-white rounded-2xl p-6 shadow-sm border border-[#1B2A4A]/5" data-id="element-74">
                <div className="flex items-start gap-4" data-id="element-75">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: `${tier.bgColor}20`
              }} data-id="element-76">
                    <CircleIcon className="w-5 h-5" style={{
                  color: tier.color
                }} fill={tier.color} data-id="element-77" />
                  </div>
                  <div className="flex-1" data-id="element-78">
                    <div className="flex items-center gap-2 mb-1" data-id="element-79">
                      <h3 className="font-semibold text-[#1B2A4A]" data-id="element-80">
                        {tier.tier}
                      </h3>
                      <span className="text-xs text-[#666]" data-id="element-81">
                        • {tier.description}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3" data-id="element-82">
                      {tier.locations.map(location => <span key={location} className="px-3 py-1 rounded-full text-sm" style={{
                    backgroundColor: `${tier.bgColor}15`,
                    color: tier.color
                  }} data-id="element-83">
                          {location}
                        </span>)}
                    </div>
                  </div>
                </div>
              </motion.div>)}

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.7
          }} className="bg-[#1B2A4A] rounded-2xl p-6 text-white" data-id="element-84">
              <p className="text-sm leading-relaxed" data-id="element-85">
                We use a{' '}
                <span className="text-[#C9A96E] font-semibold" data-id="element-86">
                  15–20 mile radius
                </span>{' '}
                around your clinic and only target people who are{' '}
                <span className="text-[#C9A96E] font-semibold" data-id="element-87">
                  physically in these areas
                </span>{' '}
                — not people who are merely "interested in" the area from
                elsewhere.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}